import React, { useCallback, useEffect } from 'react';
import { TouchableOpacity } from 'react-native';

import { ModalBox } from 'components/base/modal';
import { Colors } from 'theme/colors';
import { Light, Regular } from 'theme/text';
import { Bubble } from 'components/base/bubble';
import { ContactButton } from 'components/chat/buttons/contact-us';
import { ErrorContainer } from './styles';

/**
 * Show inline chat error
 *
 * @param props - Error props
 */
export function InlineSubmitError(props: InlineSubmitErrorProps) {
  const { visible, message } = props;

  if (!visible) {
    return null;
  }

  return (
    <ErrorContainer>
      <Bubble>
        <Regular textAlign="center">{message}</Regular>
      </Bubble>
    </ErrorContainer>
  );
}
/**
 * Show inline chat error
 *
 * @param props - Error props
 */
export function InlineSubmitErrorRetry(props: InlineSubmitErrorRetryProps) {
  const { visible, message, onRetry, accentColor } = props;
  const tryAgainColor = accentColor ?? Colors.green;

  if (!visible) {
    return null;
  }

  return (
    <ErrorContainer>
      <Bubble>
        <Light textAlign="center">
          {message}
          {' Please '}
          <TouchableOpacity onPress={onRetry}>
            <Regular color={tryAgainColor}>try again.</Regular>
          </TouchableOpacity>
        </Light>
      </Bubble>
    </ErrorContainer>
  );
}
/**
 * Renders an inline error message when the are issues
 * submitting the next message.
 *
 * @param props - The error props
 */
export function InlineChatError(props: Props) {
  const { visible, onRetry, retries, onClearRetry, onHelp, accentColor } = props;
  const onContact = useCallback(() => {
    ModalBox.hide(onHelp);
  }, [onHelp]);

  useEffect(() => {
    if (retries > 2) {
      ModalBox.show('inlineChatError', {
        isBackdropClickable: false,
        onClose: onClearRetry,
        content: <ContactButton onPress={onContact} />,
        onSubmit: () => {
          onRetry();
          onClearRetry();
        },
      });
    }
  }, [onClearRetry, retries, onRetry, onContact]);

  if (!visible || retries > 2) {
    return null;
  }

  return (
    <InlineSubmitErrorRetry
      message="We had a hard time submitting your answer."
      accentColor={accentColor}
      visible={visible}
      onRetry={onRetry}
    />
  );
}
interface Props {
  /** Indicates whether to show the component or not  */
  visible?: boolean;
  /** Handles try actions  */
  onRetry: () => void;
  /** Emits the `Help` action */
  onHelp: () => void;
  /** Emits the `ClearRetry`  event which clears the retry count */
  onClearRetry: () => void;
  /** The number of times the user has retried */
  retries: number;
  /** The color with which to override accentuated text */
  accentColor?: string;
}
interface InlineSubmitErrorProps {
  /** Indicates whether to show the component or not  */
  visible?: boolean;
  /** The error message */
  message: string;
}

interface InlineSubmitErrorRetryProps {
  /** Handles try actions  */
  onRetry: () => void;
  /** Indicates whether to show the component or not  */
  visible?: boolean;
  /** The error message */
  message: string;
  /** The color with which to override accentuated text */
  accentColor?: string;
}
